import { useEffect, useState } from 'react';

import Box from '~/common/components/Box';
import Flex from '~/common/components/Flex';
import Spinner from '~/common/components/GlobalLayout/Spinner';
import Text from '~/common/components/Texts/Text';
import { formatCurrency } from '~/common/util/format';
import {
  isEmploymentIncome,
  isRentalIncome,
  useIncomeInformation,
} from '~/pages/IncomeCalculator/hooks';
import lang from '~/pages/IncomeCalculator/language.json';

import Alerts from '../../Pages/ReviewBreakdown/Alerts';
import CalculationCards from '../../Pages/ReviewBreakdown/Wrapper/CalculationCards';
import useCalculation from '../../queries/useCalculation';
import CalculationBreakdown from './CalculationBreakdown';
import CalculationHistoryError from './components/CalculationHistoryError';
import OverriddenIncomes from './components/OverridenIncomes';

type HistoricalBreakdownProps = {
  calculationId: number;
  calculationAuditId: number;
  onNewWindow: (...args: any[]) => any;
};

const HistoricalBreakdown = ({
  calculationId,
  calculationAuditId,
  onNewWindow,
}: Readonly<HistoricalBreakdownProps>) => {
  const [selectedResult, setSelectedResult] =
    useState<(typeof data)['results'][number]>(null);
  const { data: selectedIncome } = useIncomeInformation();
  const { isLoading, isError, data } = useCalculation(
    calculationId,
    calculationAuditId,
  );

  useEffect(() => {
    if (isLoading || !data?.results?.length || selectedResult) {
      return;
    }

    const defaultResult = isRentalIncome(selectedIncome)
      ? data.results[0]
      : data.results.find((item) => item.isSelected);

    setSelectedResult(defaultResult);
  }, [data, isLoading, selectedIncome, selectedResult, setSelectedResult]);

  if (isLoading) {
    return <Spinner />;
  }

  if (isError) {
    return <CalculationHistoryError />;
  }

  if (!data?.results || !selectedResult) {
    return null;
  }

  return (
    <Flex height="100%" flexDirection="column">
      <Box flexBasis="calc(100% - 36px)" sx={{ overflowY: 'scroll' }}>
        {data.results.length > 1 && (
          <CalculationCards
            calculations={data.results}
            selectedCard={selectedResult}
            onSelect={(result: typeof selectedResult) =>
              setSelectedResult(result)
            }
          />
        )}
        <Box mb={4}>
          <CalculationBreakdown
            results={data.results}
            selectedResult={selectedResult}
            onNewWindow={onNewWindow}
          />
        </Box>
        <Text mb={4} data-testid="calculated-income">
          <Text as="span" fontWeight={500}>
            {lang.CALCULATED_MONTHLY_INCOME}&nbsp;
          </Text>
          {formatCurrency(selectedResult.calculationAmount)}
        </Text>
        <OverriddenIncomes possibleOverrides={selectedResult.overrideAmounts} />
        <Alerts
          alerts={selectedResult.alerts}
          selectedIncome={isEmploymentIncome(selectedIncome) && selectedIncome}
          my={4}
        />
        {data.comment && (
          <Box>
            <Text fontWeight={500} borderBottom="2px solid" mb={4}>
              {lang.COMMENT}
            </Text>
            <Text>{data.comment}</Text>
          </Box>
        )}
      </Box>
    </Flex>
  );
};

export default HistoricalBreakdown;
